import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import useIsMobile from '../../hooks/useIsMobile';

/** Security page — wireframe shield slowly rotating with orbiting particles.
 *  Glowing green (#00E5CC) + violet (#7B61FF). Mouse rotates the shield.
 */
const ShieldOrbitBg = () => {
    const mountRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) return;
        const mount = mountRef.current;
        if (!mount) return;
        const w = mount.clientWidth, h = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 200);
        camera.position.set(0, 0, 18);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        // Shield shape — extruded svg-like path
        const shieldShape = new THREE.Shape();
        shieldShape.moveTo(0, 5);
        shieldShape.bezierCurveTo(3.5, 4.8, 4, 3, 4, 1.5);
        shieldShape.lineTo(4, -1);
        shieldShape.bezierCurveTo(4, -3.5, 2, -5, 0, -5.5);
        shieldShape.bezierCurveTo(-2, -5, -4, -3.5, -4, -1);
        shieldShape.lineTo(-4, 1.5);
        shieldShape.bezierCurveTo(-4, 3, -3.5, 4.8, 0, 5);

        const extrude = new THREE.ExtrudeGeometry(shieldShape, {
            depth: 0.4,
            bevelEnabled: false,
            curveSegments: 24,
        });
        const wireGeo = new THREE.EdgesGeometry(extrude);

        const shieldGroup = new THREE.Group();
        // Outer violet wire
        const wireMat1 = new THREE.LineBasicMaterial({
            color: 0x7B61FF,
            transparent: true,
            opacity: 0.85,
        });
        const wireShield1 = new THREE.LineSegments(wireGeo, wireMat1);
        shieldGroup.add(wireShield1);
        // Inner teal wire (slightly larger for glow)
        const wireMat2 = new THREE.LineBasicMaterial({
            color: 0x00E5CC,
            transparent: true,
            opacity: 0.5,
        });
        const wireShield2 = new THREE.LineSegments(wireGeo, wireMat2);
        wireShield2.scale.set(1.06, 1.06, 1.06);
        shieldGroup.add(wireShield2);

        // Subtle fill
        const fillMat = new THREE.MeshBasicMaterial({
            color: 0x00E5CC,
            transparent: true,
            opacity: 0.08,
        });
        const shieldFill = new THREE.Mesh(extrude, fillMat);
        shieldGroup.add(shieldFill);
        scene.add(shieldGroup);

        // Orbiting particles
        const particleCount = 200;
        const pGeo = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const userData = [];
        for (let i = 0; i < particleCount; i++) {
            const r = 7 + Math.random() * 2;
            const a = Math.random() * Math.PI * 2;
            const incl = (Math.random() - 0.5) * 0.6;
            userData.push({ r, a, speed: 0.15 + Math.random() * 0.4, incl });
            positions[i * 3] = r * Math.cos(a);
            positions[i * 3 + 1] = r * Math.sin(a) * incl;
            positions[i * 3 + 2] = r * Math.sin(a);
        }
        pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        const pMat = new THREE.PointsMaterial({
            color: 0x00E5CC,
            size: 0.08,
            transparent: true,
            opacity: 0.85,
        });
        const particles = new THREE.Points(pGeo, pMat);
        scene.add(particles);

        // Mouse parallax
        const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
            mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
        };
        window.addEventListener('mousemove', onMouseMove);

        const onResize = () => {
            const W = mount.clientWidth, H = mount.clientHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        const clock = new THREE.Clock();
        let raf = 0;
        const tick = () => {
            raf = requestAnimationFrame(tick);
            const t = clock.getElapsedTime();
            const dt = clock.getDelta();
            mouse.x += (mouse.tx - mouse.x) * 0.05;
            mouse.y += (mouse.ty - mouse.y) * 0.05;

            shieldGroup.rotation.y = t * 0.18 + mouse.x * 0.8;
            shieldGroup.rotation.x = Math.sin(t * 0.2) * 0.1 + mouse.y * 0.6;

            // Orbit particles
            const arr = pGeo.attributes.position.array;
            for (let i = 0; i < particleCount; i++) {
                const u = userData[i];
                u.a += u.speed * dt;
                arr[i * 3] = u.r * Math.cos(u.a);
                arr[i * 3 + 1] = u.r * Math.sin(u.a) * u.incl;
                arr[i * 3 + 2] = u.r * Math.sin(u.a);
            }
            pGeo.attributes.position.needsUpdate = true;
            particles.rotation.y = -t * 0.05;
            particles.rotation.x = Math.PI / 6;

            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            extrude.dispose();
            wireGeo.dispose();
            wireMat1.dispose();
            wireMat2.dispose();
            fillMat.dispose();
            pGeo.dispose();
            pMat.dispose();
            renderer.dispose();
        };
    }, [isMobile]);

    if (isMobile) {
        return (
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 50% 45%, rgba(56,189,248,0.14), transparent 55%), radial-gradient(circle at 50% 45%, rgba(37,99,235,0.10), transparent 60%)',
                }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
            data-testid="bg-shield-orbit"
        />
    );
};

export default ShieldOrbitBg;
